/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import React, { useCallback, useMemo } from "react";

import { usePlayerCallbacks } from "../../../../../shared/context/PlayerCallbacksContext";
import { usePlayerComponents } from "../../../../../shared/context/PlayerComponentsContext";
import { usePlayerTranslation } from "../../../../../shared/context/PlayerTranslationContext";
import ErrorIcon from "../../../../../shared/icons/general/Error.svg";
import {
  usePlayerController,
  UserPlayerErrorMessage,
} from "../../../../BasePlayer";
import { DialogBox } from "../../../../Interface/Base";

import "./ErrorDialog.scss";

interface ErrorDialogProps {
  error: UserPlayerErrorMessage;
}

export const ErrorDialog = (props: ErrorDialogProps) => {
  const {
    error: { type, message },
  } = props;

  const { player } = usePlayerController();
  const { onClosePlayer } = usePlayerCallbacks();
  const { Button } = usePlayerComponents();

  const { t } = usePlayerTranslation();

  const onPlayerClose = useCallback(() => onClosePlayer?.(), [onClosePlayer]);
  const onRetry = useCallback(() => player.reload(), [player]);

  const content = useMemo(() => {
    return (
      <>
        <div className="PlayerErrorDialog__ErrorIcon">
          <ErrorIcon aria-hidden="true" focusable="false" />
        </div>
        <p>{message}</p>
        <span>{t("PLAYER_ERROR_CODE", { code: type })}</span>
      </>
    );
  }, [type, message, t]);

  const actions = useMemo(() => {
    return (
      <>
        <Button variant="secondary" onClick={onPlayerClose}>
          {t("PLAYER_BUTTON_ERROR_CLOSE_PLAYER")}
        </Button>
        <Button variant="primary" onClick={onRetry}>
          {t("PLAYER_BUTTON_ERROR_RETRY")}
        </Button>
      </>
    );
  }, [Button, onPlayerClose, onRetry, t]);

  return <DialogBox content={content} actions={actions} />;
};
